/**
 * A folha do "+" — só criações (decisão do PO, 17/09/2026, lote de aparência 1).
 *
 * Cinco sempre: Nova despesa · Nova atividade · Enviar foto · Nova enquete ·
 * Novo item de mala. Duas por permissão (senão nem aparecem): Alertar o grupo ·
 * Ponto de encontro. Nada aqui cria por si: cada item leva à tela que já
 * cria, com o campo certo em foco quando a tela souber (`?novo=1`).
 *
 * "Nova atividade" abre o dia de hoje no fuso da viagem; antes da viagem,
 * o primeiro dia; depois dela, o último — o formulário carrega o dia por
 * conta própria (`AtividadeFormScreen`).
 */
import { featureEnabled, resolveToday } from '@nv/core';
import { useNavigate } from 'react-router-dom';

import { useAgora } from '../../demo/DemoContext';
import { usePermissao, useTrip } from '../../trip/TripContext';
import { useDiasDaViagem } from '../../trip/dias';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon, type IconName } from '../../ui/icons';
import { Ilustracao, type NomeIlustracao } from '../../ui/Ilustracao';
import { Sheet } from '../../ui/Sheet';

interface Criacao {
  id: string;
  rotulo: string;
  icone: IconName;
  /** Refino final: cada criação tem o seu desenho; sem ele, o ícone num círculo. */
  ilustracao?: NomeIlustracao;
  para: string;
}

export function diaParaNovaAtividade(dias: { date: string }[] | null | undefined, hoje: string | null): string | null {
  if (!dias || dias.length === 0) return null;
  const ordenados = [...dias].sort((a, b) => a.date.localeCompare(b.date));
  const primeiro = ordenados[0]!.date;
  const ultimo = ordenados[ordenados.length - 1]!.date;
  if (!hoje || hoje < primeiro) return primeiro;
  if (hoje > ultimo) return ultimo;
  return ordenados.find((d) => d.date === hoje)?.date ?? primeiro;
}

export function CriarSheet({ onFechar }: { onFechar: () => void }) {
  const navigate = useNavigate();
  const { trip, members } = useTrip();
  // Aviso rápido e ponto de encontro: só quem administra, e só com a
  // funcionalidade ligada na viagem (como na Home e no Mapa).
  const podeAvisar = usePermissao('manage_trip_settings') && featureEnabled(trip.features, 'ponto_de_encontro');
  const viagem = tripInputDaViagem(trip, members.length);
  const agora = useAgora(viagem);
  const dias = useDiasDaViagem(trip.id);

  const hoje = (() => {
    if (!viagem) return null;
    try {
      return resolveToday(viagem.timeZone, agora, viagem.utcOffsetSchedule);
    } catch {
      return null;
    }
  })();
  const diaAtividade = diaParaNovaAtividade(dias.days, hoje);

  const sempre: Criacao[] = [
    { id: 'despesa', rotulo: 'Nova despesa', icone: 'dinheiro', ilustracao: 'despesa', para: '/financeiro/nova' },
    { id: 'atividade', rotulo: 'Nova atividade', icone: 'roteiro', ilustracao: 'atividade', para: diaAtividade ? `/roteiro/${diaAtividade}/nova` : '/roteiro' },
    { id: 'foto', rotulo: 'Enviar foto', icone: 'memorias', ilustracao: 'memorias', para: '/memorias/enviar' },
    { id: 'enquete', rotulo: 'Nova enquete', icone: 'enquete', ilustracao: 'enquetes', para: '/enquetes?novo=1' },
    { id: 'mala', rotulo: 'Novo item de mala', icone: 'mala', ilustracao: 'bagagem', para: '/bagagem?novo=1' },
    { id: 'sugestao', rotulo: 'Nova sugestão', icone: 'externo', ilustracao: 'sugestao', para: '/sugestoes?novo=1' },
  ];
  const porPermissao: Criacao[] = podeAvisar
    ? [
        { id: 'aviso', rotulo: 'Alertar o grupo', icone: 'notificacoes', para: '/?aviso=1' },
        { id: 'encontro', rotulo: 'Ponto de encontro', icone: 'ponto', ilustracao: 'mapa', para: '/mapa?encontro=1' },
      ]
    : [];

  const ir = (para: string) => {
    onFechar();
    navigate(para);
  };

  return (
    <Sheet titleId="criar-titulo" onFechar={onFechar} className="criar" testId="criar-sheet">
      <h2 id="criar-titulo" className="sheet__title">
        Criar
      </h2>
      {/* Refino final: grade de 3, ilustração de 56, itens entrando em sequência. */}
      <ul className="criar__grade">
        {[...sempre, ...porPermissao].map((c, i) => (
          <li key={c.id} className="nv-enter" style={{ ['--i' as string]: i + 2 }}>
            <button type="button" className="criar__item" data-testid={`criar-${c.id}`} onClick={() => ir(c.para)}>
              {c.ilustracao ? (
                <Ilustracao nome={c.ilustracao} tamanho={56} />
              ) : (
                <span className="criar__icone" aria-hidden="true">
                  <Icon name={c.icone} size={24} />
                </span>
              )}
              <span className="criar__rotulo">{c.rotulo}</span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
